import React, { useState, useEffect } from 'react';
import './labs.css';
import Blood from './blood';
import Serum from './serum';
import CSF from './csf';
import Urine from './urine';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Search } from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../auth/firebase';
import { useAuthStore } from '../../../auth/state';
import cacheManager from '../../../auth/cache_manager';

const Labs = () => {
  const [activeTab, setActiveTab] = useState('blood');
  const [searchTerms, setSearchTerms] = useState({
    blood: '',
    serum: '',
    csf: '',
    urine: ''
  });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      if (user && user.uid) {
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  const tabs = [
    { id: 'blood', label: 'Blood' },
    { id: 'serum', label: 'Serum' },
    { id: 'csf', label: 'CSF' },
    { id: 'urine', label: 'Urine' }
  ];

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);

    if (isLeftSwipe && currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
    if (isRightSwipe && currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    const handleScroll = (e) => {
      const container = e.target;
      const isScrollLeft = container.scrollLeft > 0;
      const isScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);

      container.classList.toggle('scroll-left', isScrollLeft);
      container.classList.toggle('scroll-right', isScrollRight);
    };

    const tableContainers = document.querySelectorAll('.labs_table_container');
    tableContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll);
      handleScroll({ target: container });
    });

    return () => {
      tableContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, [activeTab]);

  const handleSearchChange = (e) => {
    setSearchTerms(prev => ({
      ...prev,
      [activeTab]: e.target.value
    }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const renderContent = () => {
    const props = {
      searchTerm: searchTerms[activeTab],
      isDarkMode
    };

    switch(activeTab) {
      case 'blood':
        return <Blood {...props} />;
      case 'serum':
        return <Serum {...props} />;
      case 'csf':
        return <CSF {...props} />;
      case 'urine':
        return <Urine {...props} />;
      default:
        return <Blood {...props} />;
    }
  };

  return (
    <div className={`ext_lbs_labs_wrapper ${isDarkMode ? 'ext_lbs_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="ext_lbs_labs-container">
        <div className="ext_lbs_labs-header">
          <h1>Laboratory Reference Values</h1>
          <p>NBME-Based laboratory values and their reference ranges</p>
        </div>

        <div className="ext_lbs_labs-search">
          <div className="ext_lbs_labs-search_container">
            <Search className="ext_lbs_labs-search_icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab} values...`}
              value={searchTerms[activeTab]}
              onChange={handleSearchChange}
              className="ext_lbs_labs-search_input"
            />
          </div>
        </div>

        <div className="ext_lbs_labs-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`ext_lbs_labs-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
              {searchTerms[tab.id] && (
                <span className="ext_lbs_labs-search_indicator">•</span>
              )}
            </button>
          ))}
        </div>

        <div 
          className="ext_lbs_labs-content"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {renderContent()}
        </div>
      </div>
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Labs;
